export function formatUTCtoLocalDate(
  utcTimestamp: any,
  locale: string = "en-GB",
) {
  const date = new Date(utcTimestamp);
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
  };
  return new Intl.DateTimeFormat(locale, options).format(date);
}

export function formateDateOnly(localDate: string, locale: string = "en-GB") {
  const date = new Date(localDate);
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
  };
  return new Intl.DateTimeFormat(locale, options).format(date);
}

export function formatDate(dateString: string) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

export interface TimeFormatOptions {
  hour12?: boolean;
}

export function formatTimeOnly(
  utcTimestamp: string | number | Date,
  options?: TimeFormatOptions
): string {
  const date = new Date(utcTimestamp);
  const hour12 = options?.hour12 ?? false;

  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12,
  };

  return date.toLocaleTimeString(hour12 ? "en-US" : "ru-RU", formatOptions);
}

export interface FormatLastSeenOptions {
  translate?: (key: string) => string;
  hour12?: boolean;
}

export function formatLastSeen(
  utcDate: Date | string,
  options?: FormatLastSeenOptions
): string {
  if (typeof utcDate === "string") {
    utcDate = new Date(utcDate);
  }

  if (!(utcDate instanceof Date) || isNaN(utcDate.getTime())) {
    return "";
  }

  const translate = options?.translate ?? ((k: string) => k);
  const hour12 = options?.hour12 ?? false;

  const lastSeenDate = new Date(utcDate);
  const now = new Date();

  const formatTime = (date: Date): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const minutesStr = minutes < 10 ? "0" + minutes : minutes;

    if (hour12) {
      const h = hours % 12 || 12;
      const ampm = hours >= 12 ? "PM" : "AM";
      return `${h}:${minutesStr} ${ampm}`;
    }
    return `${hours}:${minutesStr}`;
  };

  const isToday = lastSeenDate.toDateString() === now.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = lastSeenDate.toDateString() === yesterday.toDateString();

  const timeString = formatTime(lastSeenDate);

  if (isToday) {
    return `${translate("Last seen today at")} ${timeString}`;
  } else if (isYesterday) {
    return `${translate("Last seen yesterday at")} ${timeString}`;
  } else {
    const day = String(lastSeenDate.getDate()).padStart(2, "0");
    const month = String(lastSeenDate.getMonth() + 1).padStart(2, "0");
    const year = lastSeenDate.getFullYear();
    return `${translate("Last seen")} ${day}/${month}/${year} ${translate("at")} ${timeString}`;
  }
}

// export function formatLastSeen(utcDate: Date | string): string {

//   // Convert string to Date if needed
//   if (typeof utcDate === "string") {
//     utcDate = new Date(utcDate);
//   }

//   if (!(utcDate instanceof Date) || isNaN(utcDate.getTime())) {
//     return "";
//   }

//   const lastSeenDate = new Date(utcDate);
//   const now = new Date();

//   const diffInMinutes = Math.floor(
//     (now.getTime() - lastSeenDate.getTime()) / (1000 * 60),
//   );
//   const diffInHours = Math.floor(diffInMinutes / 60);
//   const diffInDays = Math.floor(diffInHours / 24);
//   const diffInMonths = Math.floor(diffInDays / 30); // Approximation for months

//   if (diffInMinutes < 1) {
//     return "Last Seen Just now";
//   } else if (diffInMinutes < 60) {
//     return `Last Seen ${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
//   } else if (diffInHours < 24) {
//     return `Last Seen ${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
//   } else if (diffInDays < 30) {
//     return `Last Seen ${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
//   } else if (diffInMonths < 12) {
//     return `Last Seen ${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`;
//   } else {
//     const years = Math.floor(diffInMonths / 12);
//     return `Last Seen ${years} year${years > 1 ? "s" : ""} ago`;
//   }
// }

export interface FormatRelativeTimeOptions {
  translate?: (key: string) => string;
  hour12?: boolean;
}

export function formatRelativeTime(
  utcDateString: Date,
  options?: FormatRelativeTimeOptions
) {
  const date = new Date(utcDateString);
  const now = new Date();
  const translate = options?.translate ?? ((k: string) => k);
  const hour12 = options?.hour12 ?? false;

  const localDate = new Date(
    date.toLocaleString("en-US", {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  );
  const localNow = new Date(
    now.toLocaleString("en-US", {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  );

  const dateDay = localDate.getDate();
  const nowDay = localNow.getDate();
  const dateMonth = localDate.getMonth();
  const nowMonth = localNow.getMonth();
  const dateYear = localDate.getFullYear();
  const nowYear = localNow.getFullYear();

  const formattedTime = localDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12,
  });

  if (dateYear === nowYear && dateMonth === nowMonth) {
    if (dateDay === nowDay) {
      return `${translate("Today")}, ${formattedTime}`;
    } else if (dateDay === nowDay - 1) {
      return `${translate("Yesterday")}, ${formattedTime}`;
    }
  }

  return (
    localDate.toLocaleDateString("en-US", { month: "long", day: "numeric" }) +
    `, ${formattedTime}`
  );
}

export const formatTime = (
  isoString: any,
  options?: TimeFormatOptions
): string => {
  const date = new Date(isoString);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const hour12 = options?.hour12 ?? false;

  if (hour12) {
    const h = hours % 12 || 12;
    const ampm = hours >= 12 ? "pm" : "am";
    return `${h}:${minutes} ${ampm}`;
  }
  return `${hours}:${minutes}`;
};

export function formatCallDuration(
  seconds: number,
  languageCode: string = "en",
) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const days = Math.floor(total / 86400);
  const hrs = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  const isRu = String(languageCode).toLowerCase().startsWith("ru");
  const parts: string[] = [];

  if (isRu) {
    if (days > 0) parts.push(`${days} д.`);
    if (hrs > 0) parts.push(`${hrs} ч.`);
    if (mins > 0) parts.push(`${mins} мин.`);
    // Seconds: alone under 1 min, or when no higher unit shown besides days/hrs with 0 mins
    if (secs > 0 && (days === 0 && hrs === 0 || mins === 0)) {
      parts.push(`${secs} сек.`);
    }
    if (parts.length === 0) parts.push("0 сек.");
    return parts.join(" ");
  }

  if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  if (hrs > 0) parts.push(`${hrs} ${hrs === 1 ? "hr" : "hrs"}`);
  if (mins > 0) parts.push(`${mins} ${mins === 1 ? "min" : "mins"}`);
  if (secs > 0 && (days === 0 && hrs === 0 || mins === 0)) {
    parts.push(`${secs} sec`);
  }
  if (parts.length === 0) parts.push("0 sec");
  return parts.join(", ");
}
